import { useState, useEffect } from 'react';
import { Modal, Row, Col, Table } from 'react-bootstrap';
import { Tab, Tabs } from 'react-bootstrap';
import { inject } from 'mobx-react';
import moment from 'moment';

//components
import withLocalization from '~/hoc/withLocalization';
import { SimpleSelectWidget } from '../../../components/GenericForm/Widgets/SimpleSelect.js';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import DatePicker from '~/components/DatePicker/DatePicker.jsx';

//elements
import RequiredStar from '../../../elements/RequiredStar.js';
import LoadingSpinner from '../../../elements/LoadingSpinner.js';

//utils
import { isSunday, isMonday } from '../../../utils/constants.js';
import { shortDateFormat, getShortDateFormat } from '../../../utils/dateFormat';

//assets
import CloseIcon from "~/assets/img/modal-close-icon.svg";
import Delete from '../../../assets/img/deleting.svg';


const BulkFailureTable = ({ t, failed, userNameMap }) => (
    <div className='projectMemberForm mt-2'>
        <div className='mb-1 text-muted small'>{t('We found some issues while assigning the settings')}</div>
        <Table className='w-100 table-scroll'>
            <thead>
                <tr>
                    <th width={'40%'} className='text-start'>{t('Member')}</th>
                    <th width={'60%'}>{t('Issue')}</th>
                </tr>
            </thead>
            <tbody>
                {failed.map((r, idx) => (
                    <tr key={idx}>
                        <td className='text-start'>{userNameMap?.[r.user_id] || `User ${r.user_id}`}</td>
                        <td className='timelog_array_details text-danger'>{r.error}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    </div>
);

const UserWeeklyOvertimeMapTab = (props) => {
    const { t, userId, userIds, userNameMap, userStore, commonStore, onClose } = props;
    const isBulk = Array.isArray(userIds) && userIds.length > 0;
    const dateTimeRules = commonStore.getDateTimeRules();

    const weeklyOvertimeOptions = commonStore.weeklyOvertimeSettings?.map(s => ({
        value: s.id,
        label: s.name + (s.is_active ? "" : ` (${t("Inactive")})`),
        isDisabled: !s.is_active,
        valid_from: s.valid_from,
        valid_to: s.valid_to
    })) || [];

    const [loading, setLoading] = useState(false);
    const [userWeeklyOvertimeMaps, setUserWeeklyOvertimeMaps] = useState([]);
    const [isSubmitBtnClicked, setSubmitBtnStatus] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);
    const [currentEditing, setCurrentEditing] = useState({
        weekly_overtime_id: null,
        valid_from: null,
        valid_to: null,
    });



    useEffect(() => {
        if (!isBulk) {
            setLoading(true);
            loadData();
        }
    }, []);

    const loadData = async (resetForm = false) => {
        try {
            const data = await userStore.getUserWeeklyOvertimeMap(userId);
            setUserWeeklyOvertimeMaps(data.userWeeklyOvertimeMap || []);
            setLoading(false);
            if (resetForm) {
                setSubmitBtnStatus(false);
                setCurrentEditing({ weekly_overtime_id: null, valid_from: null, valid_to: null });
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
        }
    };

    const handleChange = (name, value) => {
        if (name === 'weekly_overtime_id') {
            const weeklyOvertime = weeklyOvertimeOptions.find(o => o.value === value);
            if (!weeklyOvertime) return;
            setCurrentEditing({
                ...currentEditing,
                [name]: value,
                valid_from: weeklyOvertime.valid_from || null,
                valid_to: weeklyOvertime.valid_to || null
            });
            return;
        }
        setCurrentEditing({ ...currentEditing, [name]: value });
    };

    const handleSubmit = async () => {
        if (!currentEditing.weekly_overtime_id)
            return commonStore.addNotification(t('Please select a weekly overtime rule'), null, 'warning');
        if (!currentEditing.valid_from)
            return commonStore.addNotification(t('Please select a valid from date'), null, 'warning');

        setSubmitBtnStatus(true);
        try {
            const res = await userStore.saveUserWeeklyOvertimeMap({
                user_ids: isBulk ? userIds : [userId],
                weekly_overtime_id: currentEditing.weekly_overtime_id,
                valid_from: moment(currentEditing.valid_from).format('YYYY-MM-DD'),
                valid_to: currentEditing.valid_to ? moment(currentEditing.valid_to).format('YYYY-MM-DD') : null,
            });

            if (isBulk) {
                const failed = res.results.filter(r => !r.success);
                if (failed.length > 0) {
                    setBulkResults({ failed });
                    commonStore.addNotification(t('Weekly overtime rule partially assigned successfully'), null, 'success');
                    setSubmitBtnStatus(false);
                    setCurrentEditing({ weekly_overtime_id: null, valid_from: null, valid_to: null });
                } else {
                    commonStore.addNotification(t('Weekly overtime rule assigned successfully'), null, 'success');
                    if (onClose) onClose();
                }
            } else {
                const result = res.results[0];
                if (!result.success) throw new Error(result.error);
                commonStore.addNotification(t('Weekly overtime rule assigned successfully'), null, 'success');
                loadData(true);
            }
        } catch (err) {
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
            setSubmitBtnStatus(false);
        }
    };

    const handleDeactivate = async (id) => {
        try {
            setLoading(true);
            await userStore.deactivateUserWeeklyOvertimeMap(id);
            commonStore.addNotification(t('Rule deactivated successfully'), null, 'success');
            loadData();
        } catch (err) {
            setLoading(false);
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
        }
    };

    return (
        <Row className='mt-3'>
            <Row className='gx-0 align-items-end mb-3'>
                <Col xs={4} className='pe-2'>
                    <label className='control-label'>{t('Weekly Overtime Rule')} <RequiredStar /></label>
                    <SimpleSelectWidget
                        options={weeklyOvertimeOptions}
                        placeholder={t('Select Rule')}
                        value={currentEditing.weekly_overtime_id}
                        onChange={(val) => handleChange('weekly_overtime_id', val)}
                    />
                </Col>
                <Col xs={3} className='pe-2'>
                    <label className='control-label'>{t('Valid From')} <RequiredStar /></label>
                    <DatePicker
                        selected={currentEditing.valid_from ? new Date(moment(currentEditing.valid_from).format('MM/DD/YYYY')) : null}
                        placeholder={t('Select Monday')}
                        dateFormat={getShortDateFormat(dateTimeRules)}
                        onChange={(val) => handleChange('valid_from', val)}
                        filterDate={isMonday}
                        minDate={currentEditing?.weekly_overtime_id && weeklyOvertimeOptions.find(o => o.value === currentEditing.weekly_overtime_id).valid_from ? new Date(weeklyOvertimeOptions.find(o => o.value === currentEditing.weekly_overtime_id).valid_from) : null}
                    />
                </Col>
                <Col xs={3} className='pe-2'>
                    <label className='control-label'>{t('Valid To')}</label>
                    <DatePicker
                        selected={currentEditing.valid_to ? new Date(moment(currentEditing.valid_to).format('MM/DD/YYYY')) : null}
                        placeholder={t('Select Sunday')}
                        dateFormat={getShortDateFormat(dateTimeRules)}
                        onChange={(val) => handleChange('valid_to', val)}
                        filterDate={isSunday}
                        minDate={currentEditing?.weekly_overtime_id && weeklyOvertimeOptions.find(o => o.value === currentEditing.weekly_overtime_id).valid_from ? new Date(weeklyOvertimeOptions.find(o => o.value === currentEditing.weekly_overtime_id).valid_from) : currentEditing.valid_from ? new Date(currentEditing.valid_from) : null}
                        maxDate={currentEditing?.weekly_overtime_id && weeklyOvertimeOptions.find(o => o.value === currentEditing.weekly_overtime_id).valid_to ? new Date(weeklyOvertimeOptions.find(o => o.value === currentEditing.weekly_overtime_id).valid_to) : null}
                    />
                </Col>
                <Col xs={2} className='d-flex align-items-end'>
                    <Button fill wd onClick={handleSubmit} disabled={isSubmitBtnClicked}>
                        <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Add')}
                    </Button>
                </Col>
            </Row>

            {isBulk && bulkResults && bulkResults.failed.length > 0 && (
                <BulkFailureTable t={t} failed={bulkResults.failed} userNameMap={userNameMap} />
            )}

            <Table hidden={isBulk} className='w-100' responsive hover>
                <thead>
                    <tr>
                        <th width={'5%'}>{t('#')}</th>
                        <th width={'35%'} className='text-start'>{t('Rule Name')}</th>
                        <th width={'20%'} className='text-start'>{t('Valid From')}</th>
                        <th width={'20%'} className='text-start'>{t('Valid To')}</th>
                        <th width={'10%'} className='text-start'>{t('Status')}</th>
                        <th width={'10%'} className='text-center'>{t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={6}><LoadingSpinner /></td>
                        </tr>
                    ) : userWeeklyOvertimeMaps.length ? userWeeklyOvertimeMaps.map((map, idx) => (
                        <tr key={map.id} className='mt-1'>
                            <td className='timelog_array_details'>{idx + 1}</td>
                            <td className='timelog_array_details text-start'>
                                {map.Weekly_Overtime_Setting?.name || '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {map.valid_from ? shortDateFormat(map.valid_from, dateTimeRules) : '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {map.valid_to ? shortDateFormat(map.valid_to, dateTimeRules) : t('No end date')}
                            </td>
                            <td className='timelog_array_details text-start'>
                                <span className={`badge ${map.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                    {map.is_active ? t('Active') : t('Inactive')}
                                </span>
                            </td>
                            <td className='text-center'>
                                <Button
                                    icon_sm_delete fill
                                    title={map.is_active ? t('Deactivate') : t('Already inactive')}
                                    disabled={!map.is_active}
                                    onClick={() => handleDeactivate(map.id)}
                                >
                                    <img src={Delete} alt="deactivate" />
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={6} className='pt-4'>
                                <h5><b>{t('No weekly overtime rules assigned yet')}.</b></h5>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </Row>
    );
};

const UserOvertimeCapMapTab = (props) => {
    const { t, userId, userIds, userNameMap, userStore, commonStore, onClose } = props;
    const isBulk = Array.isArray(userIds) && userIds.length > 0;
    const dateTimeRules = commonStore.getDateTimeRules();

    const overtimeCapOptions = commonStore.weeklyOvertimeCapSettings ? commonStore.weeklyOvertimeCapSettings.map(s => ({
        value: s.id,
        label: s.name + (s.is_active ? "" : ` (${t("Inactive")})`),
        isDisabled: !s.is_active,
        valid_from: s.valid_from,
        valid_to: s.valid_to
    })) : [];

    const [loading, setLoading] = useState(false);
    const [userOvertimeCapMaps, setUserOvertimeCapMaps] = useState([]);
    const [isSubmitBtnClicked, setSubmitBtnStatus] = useState(false);
    const [bulkResults, setBulkResults] = useState(null);
    const [currentEditing, setCurrentEditing] = useState({
        overtime_cap_setting_id: null,
        valid_from: null,
        valid_to: null,
    });

    useEffect(() => {
        if (!isBulk) {
            setLoading(true);
            loadData();
        }
    }, []);

    const loadData = async (resetForm = false) => {
        try {
            const data = await userStore.getUserOvertimeCapMap(userId);
            setUserOvertimeCapMaps(data.userOvertimeCapMap || []);
            setLoading(false);
            if (resetForm) {
                setSubmitBtnStatus(false);
                setCurrentEditing({ overtime_cap_setting_id: null, valid_from: null, valid_to: null });
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
        }
    };

    const handleChange = (name, value) => {
        if (name === 'overtime_cap_setting_id') {
            const overtimeCap = overtimeCapOptions.find(o => o.value === value);
            if (!overtimeCap) return;
            setCurrentEditing({
                ...currentEditing,
                [name]: value,
                valid_from: overtimeCap.valid_from || null,
                valid_to: overtimeCap.valid_to || null
            });
            return;
        }
        setCurrentEditing({ ...currentEditing, [name]: value });
    };

    const handleSubmit = async () => {
        if (!currentEditing.overtime_cap_setting_id)
            return commonStore.addNotification(t('Please select an overtime cap rule'), null, 'warning');
        if (!currentEditing.valid_from)
            return commonStore.addNotification(t('Please select a valid from date'), null, 'warning');
        if (currentEditing.valid_to && !moment(currentEditing.valid_from).isBefore(moment(currentEditing.valid_to)))
            return commonStore.addNotification(t('Valid from must be before valid to'), null, 'warning');

        setSubmitBtnStatus(true);
        try {
            const res = await userStore.saveUserOvertimeCapMap({
                user_ids: isBulk ? userIds : [userId],
                overtime_cap_setting_id: currentEditing.overtime_cap_setting_id,
                valid_from: moment(currentEditing.valid_from).format('YYYY-MM-DD'),
                valid_to: currentEditing.valid_to ? moment(currentEditing.valid_to).format('YYYY-MM-DD') : null,
            });

            if (isBulk) {
                const failed = res.results.filter(r => !r.success);
                if (failed.length > 0) {
                    setBulkResults({ failed });
                    commonStore.addNotification(t('Overtime cap rule partially assigned successfully'), null, 'warning');
                    setSubmitBtnStatus(false);
                    setCurrentEditing({ overtime_cap_setting_id: null, valid_from: null, valid_to: null });
                } else {
                    commonStore.addNotification(t('Overtime cap rule assigned successfully'), null, 'success');
                    if (onClose) onClose();
                }
            } else {
                const result = res.results[0];
                if (!result.success) throw new Error(result.error);
                commonStore.addNotification(t('Overtime cap rule assigned successfully'), null, 'success');
                loadData(true);
            }
        } catch (err) {
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
            setSubmitBtnStatus(false);
        }
    };

    const handleDeactivate = async (id) => {
        try {
            setLoading(true);
            await userStore.deactivateUserOvertimeCapMap(id);
            commonStore.addNotification(t('Rule deactivated successfully'), null, 'success');
            loadData();
        } catch (err) {
            setLoading(false);
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
        }
    };

    const selectedRule = currentEditing.overtime_cap_setting_id
        ? overtimeCapOptions.find(o => o.value === currentEditing.overtime_cap_setting_id)
        : null;

    const ruleValidFrom = selectedRule && selectedRule.valid_from
        ? new Date(selectedRule.valid_from)
        : null;

    const ruleValidTo = selectedRule && selectedRule.valid_to
        ? new Date(selectedRule.valid_to)
        : null;

    return (
        <Row className='mt-3'>
            <Row className='gx-0 align-items-end mb-3'>
                <Col xs={4} className='pe-2'>
                    <label className='control-label'>{t('Overtime Cap Rule')} <RequiredStar /></label>
                    <SimpleSelectWidget
                        options={overtimeCapOptions}
                        placeholder={t('Select Rule')}
                        value={currentEditing.overtime_cap_setting_id}
                        onChange={(val) => handleChange('overtime_cap_setting_id', val)}
                    />
                </Col>
                <Col xs={3} className='pe-2'>
                    <label className='control-label'>{t('Valid From')} <RequiredStar /></label>
                    <DatePicker
                        selected={currentEditing.valid_from ? new Date(moment(currentEditing.valid_from).format('MM/DD/YYYY')) : null}
                        placeholder={t('Select date')}
                        dateFormat={getShortDateFormat(dateTimeRules)}
                        onChange={(val) => handleChange('valid_from', val)}
                        minDate={ruleValidFrom}
                        maxDate={ruleValidTo}
                    />
                </Col>
                <Col xs={3} className='pe-2'>
                    <label className='control-label'>{t('Valid To')}</label>
                    <DatePicker
                        selected={currentEditing.valid_to ? new Date(moment(currentEditing.valid_to).format('MM/DD/YYYY')) : null}
                        placeholder={t('Select date')}
                        dateFormat={getShortDateFormat(dateTimeRules)}
                        onChange={(val) => handleChange('valid_to', val)}
                        minDate={currentEditing.valid_from ? new Date(currentEditing.valid_from) : ruleValidFrom}
                        maxDate={ruleValidTo}
                    />
                </Col>
                <Col xs={2} className='d-flex align-items-end'>
                    <Button fill wd onClick={handleSubmit} disabled={isSubmitBtnClicked}>
                        <i className="fa fa-plus-circle fa-xl margin-right-10" /> {t('Add')}
                    </Button>
                </Col>
            </Row>

            {isBulk && bulkResults && bulkResults.failed.length > 0 && (
                <BulkFailureTable t={t} failed={bulkResults.failed} userNameMap={userNameMap} />
            )}

            <Table hidden={isBulk} className='w-100' responsive hover>
                <thead>
                    <tr>
                        <th width={'5%'}>{t('#')}</th>
                        <th width={'35%'} className='text-start'>{t('Rule Name')}</th>
                        <th width={'20%'} className='text-start'>{t('Valid From')}</th>
                        <th width={'20%'} className='text-start'>{t('Valid To')}</th>
                        <th width={'10%'} className='text-start'>{t('Status')}</th>
                        <th width={'10%'} className='text-center'>{t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={6}><LoadingSpinner /></td>
                        </tr>
                    ) : userOvertimeCapMaps.length ? userOvertimeCapMaps.map((map, idx) => (
                        <tr key={map.id} className='mt-1'>
                            <td className='timelog_array_details'>{idx + 1}</td>
                            <td className='timelog_array_details text-start'>
                                {map.Overtime_Cap_Setting ? map.Overtime_Cap_Setting.name : '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {map.valid_from ? shortDateFormat(map.valid_from, dateTimeRules) : '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {map.valid_to ? shortDateFormat(map.valid_to, dateTimeRules) : t('No end date')}
                            </td>
                            <td className='timelog_array_details text-start'>
                                <span className={`badge ${map.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                    {map.is_active ? t('Active') : t('Inactive')}
                                </span>
                            </td>
                            <td className='text-center'>
                                <Button
                                    icon_sm_delete fill
                                    title={map.is_active ? t('Deactivate') : t('Already inactive')}
                                    disabled={!map.is_active}
                                    onClick={() => handleDeactivate(map.id)}
                                >
                                    <img src={Delete} alt="deactivate" />
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={6} className='pt-4'>
                                <h5><b>{t('No overtime cap rules assigned yet')}.</b></h5>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </Row>
    );
};

const UserTimesheetBlockTab = (props) => {
    const { t, userId, userStore, commonStore } = props;
    const dateTimeRules = commonStore.getDateTimeRules();

    const [loading, setLoading] = useState(false);
    const [timesheetBlocks, setTimesheetBlocks] = useState([]);
    const [isSubmitBtnClicked, setSubmitBtnStatus] = useState(false);
    const [currentEditing, setCurrentEditing] = useState({
        valid_from: null,
        valid_to: null,
        reason: '',
    });

    useEffect(() => {
        setLoading(true);
        loadData();
    }, []);

    const loadData = async (resetForm = false) => {
        try {
            const data = await userStore.getUserTimesheetBlocks(userId);
            setTimesheetBlocks(data.timesheetBlocks || []);
            setLoading(false);
            if (resetForm) {
                setSubmitBtnStatus(false);
                setCurrentEditing({ valid_from: null, valid_to: null, reason: '' });
            }
        } catch (err) {
            setLoading(false);
            console.error(err);
        }
    };

    const handleChange = (name, value) => {
        setCurrentEditing({ ...currentEditing, [name]: value });
    };

    const handleSubmit = async () => {
        if (!currentEditing.valid_from)
            return commonStore.addNotification(t('Please select a valid from date'), null, 'warning');
        if (!currentEditing.valid_to)
            return commonStore.addNotification(t('Please select a valid to date'), null, 'warning');
        if (!moment(currentEditing.valid_from).isSameOrBefore(moment(currentEditing.valid_to)))
            return commonStore.addNotification(t('Valid from must be before valid to'), null, 'warning');
        if (!currentEditing.reason || !currentEditing.reason.trim())
            return commonStore.addNotification(t('Please enter a reason for blocking'), null, 'warning');

        setSubmitBtnStatus(true);
        try {
            await userStore.saveUserTimesheetBlock({
                user_id: userId,
                valid_from: moment(currentEditing.valid_from).format('YYYY-MM-DD'),
                valid_to: moment(currentEditing.valid_to).format('YYYY-MM-DD'),
                reason: currentEditing.reason.trim(),
            });
            commonStore.addNotification(t('Timesheet block added successfully'), null, 'success');
            loadData(true);
        } catch (err) {
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
            setSubmitBtnStatus(false);
        }
    };

    const handleUnblock = async (id) => {
        try {
            setLoading(true);
            await userStore.unblockUserTimesheet(id);
            commonStore.addNotification(t('Timesheet unblocked successfully'), null, 'success');
            loadData();
        } catch (err) {
            setLoading(false);
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
        }
    };

    return (
        <Row className='mt-3'>
            {/* Form */}
            <Row className='gx-0 mb-3'>
                <Col xs={12} className='mb-2'>
                    <label className="control-label mt-2 font-color-primary">
                        {t('Block Timesheet Entry')}
                    </label>
                    <hr />
                </Col>
                <Col xs={3} className='pe-2'>
                    <label className='control-label'>{t('Valid From')} <RequiredStar /></label>
                    <DatePicker
                        selected={currentEditing.valid_from ? new Date(moment(currentEditing.valid_from).format('MM/DD/YYYY')) : null}
                        placeholder={t('Select date')}
                        dateFormat={getShortDateFormat(dateTimeRules)}
                        onChange={(val) => handleChange('valid_from', val)}
                    />
                </Col>
                <Col xs={3} className='pe-2'>
                    <label className='control-label'>{t('Valid To')} <RequiredStar /></label>
                    <DatePicker
                        selected={currentEditing.valid_to ? new Date(moment(currentEditing.valid_to).format('MM/DD/YYYY')) : null}
                        placeholder={t('Select date')}
                        dateFormat={getShortDateFormat(dateTimeRules)}
                        onChange={(val) => handleChange('valid_to', val)}
                        minDate={currentEditing.valid_from ? new Date(currentEditing.valid_from) : null}
                    />
                </Col>
                <Col xs={4} className='pe-2'>
                    <label className='control-label'>{t('Reason')} <RequiredStar /></label>
                    <textarea
                        className='form-control'
                        rows={1}
                        placeholder={t('Enter reason for blocking')}
                        value={currentEditing.reason}
                        maxLength={255}
                        onChange={(e) => handleChange('reason', e.target.value)}
                        style={{ resize: 'none' }}
                    />
                </Col>
                <Col xs={2} className='d-flex align-items-end'>
                    <Button fill wd onClick={handleSubmit} disabled={isSubmitBtnClicked}>
                        <i className="fa fa-ban fa-xl margin-right-10" /> {t('Block')}
                    </Button>
                </Col>
            </Row>

            {/* List */}
            <Table className='w-100' responsive hover>
                <thead>
                    <tr>
                        <th width={'5%'}>{t('#')}</th>
                        <th width={'15%'} className='text-start'>{t('Valid From')}</th>
                        <th width={'15%'} className='text-start'>{t('Valid To')}</th>
                        <th width={'30%'} className='text-start'>{t('Reason')}</th>
                        <th width={'15%'} className='text-start'>{t('Blocked By')}</th>
                        <th width={'10%'} className='text-start'>{t('Status')}</th>
                        <th width={'10%'} className='text-center'>{t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={7}><LoadingSpinner /></td>
                        </tr>
                    ) : timesheetBlocks.length ? timesheetBlocks.map((block, idx) => (
                        <tr key={block.id} className='mt-1'>
                            <td className='timelog_array_details'>{idx + 1}</td>
                            <td className='timelog_array_details text-start'>
                                {block.valid_from ? shortDateFormat(block.valid_from, dateTimeRules) : '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {block.valid_to ? shortDateFormat(block.valid_to, dateTimeRules) : '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {block.actions?.[0]?.reason || '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                {block.actions?.[0]?.done_by_name || '-'}
                            </td>
                            <td className='timelog_array_details text-start'>
                                <span className={`badge ${block.is_active ? 'bg-danger' : 'bg-secondary'}`}>
                                    {block.is_active ? t('Blocked') : t('Unblocked')}
                                </span>
                            </td>
                            <td className='text-center'>
                                <Button
                                    icon_sm fill
                                    title={block.is_active ? t('Unblock') : t('Already unblocked')}
                                    disabled={!block.is_active}
                                    onClick={() => handleUnblock(block.id)}
                                >
                                    <i className={`fa ${block.is_active ? 'fa-unlock' : 'fa-lock'} fa-xl`} />
                                </Button>
                            </td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan={7} className='pt-4'>
                                <h5><b>{t('No timesheet blocks found')}.</b></h5>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </Row>
    );
};

const BulkDeactivateOvertimeModalContent = (props) => {
    const { t, fieldDef, userIds, onHide, userStore, commonStore } = props;
    const dateTimeRules = commonStore.getDateTimeRules();
    const currentWeekSunday = moment().endOf('isoWeek').startOf('day').toDate();

    const [deactivationDate, setDeactivationDate] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (!deactivationDate)
            return commonStore.addNotification(t('Please select a deactivation date'), null, 'warning');

        setIsSubmitting(true);
        try {
            const payload = { userIds, deactivationDate: moment(deactivationDate).format('YYYY-MM-DD') };
            let res;
            if (fieldDef.value === 'allow_weekly_overtime') {
                res = await userStore.batchDeactivateUserWeeklyOvertimeMap(payload);
            } else if (fieldDef.value === 'allow_overtime_cap_settings') {
                res = await userStore.batchDeactivateUserOvertimeCapMap(payload);
            }
            commonStore.addNotification(res?.message || t('Rules deactivated successfully'), null, 'success');
            onHide(true);
        } catch (err) {
            commonStore.addNotification(t(err.message || 'Something went wrong'), null, 'error');
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={true} onHide={() => onHide(false)} size="md" className="stacked-modal-1060-zindex" backdropClassName="stacked-modal-backdrop-1060-zindex">
            <Modal.Header>
                <Modal.Title>{t('Deactivate')} - {t(fieldDef.label)}</Modal.Title>
                <div className='modal-header__revised__closebtn' onClick={() => onHide(false)}>
                    <img src={CloseIcon} className="cursor-pointer" alt='close_button' />
                </div>
            </Modal.Header>
            <Modal.Body className='m-2' style={{ minHeight: '320px' }}>
                <Row className='gx-0 align-items-end mb-2'>
                    <Col xs={7} className='pe-2'>
                        <label className='control-label'>{t('Deactivation Date')} <RequiredStar /></label>
                        <DatePicker
                            selected={deactivationDate}
                            placeholder={t('Select Sunday')}
                            dateFormat={getShortDateFormat(dateTimeRules)}
                            onChange={(val) => setDeactivationDate(val)}
                            filterDate={isSunday}
                            minDate={currentWeekSunday}
                        />
                    </Col>
                </Row>
                <p className='text-muted my-4'>
                    <strong>{t('Note')}:</strong>{' '}
                    {t('This action will schedule the deactivation of')} <strong>{t(fieldDef.label)}</strong>{' '}
                    {t('for')} <strong>{userIds.length}</strong> {t('Users')}.{' '}
                    {t("Each user's currently active rule will expire on the selected date.")}
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={() => onHide(false)} disabled={isSubmitting}>{t('Cancel')}</Button>
                <Button fill onClick={handleConfirm} disabled={isSubmitting || !deactivationDate}>
                    {t('Confirm Deactivation')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const BulkDeactivateOvertimeModal = inject('commonStore', 'userStore')(withLocalization(BulkDeactivateOvertimeModalContent));

const UserWeeklyOvertimeSettingModal = (props) => {
    const { t, handleModalState, onHide, userId, userIds, userNameMap, userName, userStore, commonStore, initialTab } = props;
    const isBulk = Array.isArray(userIds) && userIds.length > 0;
    const [activeTab, setActiveTab] = useState(initialTab || 'weeklyOvertimeSetting');
    const [optionsLoading, setOptionsLoading] = useState(false);

    const onClose = () => {
        if (onHide) onHide();
        else handleModalState('showUserWeeklyOvertimeSettingModal', 0, false);
    };

    useEffect(() => {
        loadAllOptions();
    }, []);

    const loadAllOptions = async () => {
        try {
            setOptionsLoading(true);
            await commonStore.loadUserOvertimeSettingOptions();
            setOptionsLoading(false);
        } catch (err) {
            setOptionsLoading(false);
            console.error(err);
        }
    };

    if (optionsLoading)
        return (
            <Modal show={true} onHide={onClose} size="lg" dialogClassName="modal-90w" className="stacked-modal-1060-zindex" backdropClassName="stacked-modal-backdrop-1060-zindex">
                <Modal.Header>
                    <Modal.Title>{t('User Overtime Settings')}{!isBulk && userName ? ` - ${userName}` : ''}</Modal.Title>
                    <div className='modal-header__revised__closebtn' onClick={onClose}>
                        <img src={CloseIcon} className="cursor-pointer" alt='close_button' />
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <LoadingSpinner />
                </Modal.Body>
            </Modal>
        );

    return (
        <Modal show={true} onHide={onClose} size="lg" dialogClassName="modal-90w" className="stacked-modal-1060-zindex" backdropClassName="stacked-modal-backdrop-1060-zindex">
            <Modal.Header>
                <Modal.Title>{t('User Overtime Settings')}{!isBulk && userName ? ` - ${userName}` : ''}</Modal.Title>
                <div className='modal-header__revised__closebtn' onClick={onClose}>
                    <img src={CloseIcon} className="cursor-pointer" alt='close_button' />
                </div>
            </Modal.Header>
            <Modal.Body className="m-2" style={{ minHeight: "80vh" }}>
                {isBulk && (
                    <p className="text-muted small mb-2">
                        <strong>{t('Note')}:</strong> {t(activeTab === 'overtimeCapSetting' ? 'Overtime Cap Settings' : 'Weekly Overtime Setting')} {t('will be applied to')} {userIds.length} {t('Users')}.
                    </p>
                )}
                <Tabs
                    activeKey={activeTab}
                    onSelect={(key) => setActiveTab(key)}
                    id="UserOvertimeSettingTabs"
                >
                    {(!initialTab || initialTab === 'weeklyOvertimeSetting') && (
                        <Tab eventKey="weeklyOvertimeSetting" title={t('Weekly Overtime Settings')}>
                            <UserWeeklyOvertimeMapTab
                                t={t}
                                userId={userId}
                                userIds={userIds}
                                userNameMap={userNameMap}
                                userStore={userStore}
                                commonStore={commonStore}
                                onClose={onClose}
                            />
                        </Tab>
                    )}
                    {(!initialTab || initialTab === 'overtimeCapSetting') && (
                        <Tab eventKey="overtimeCapSetting" title={t('Overtime Cap Settings')}>
                            <UserOvertimeCapMapTab
                                t={t}
                                userId={userId}
                                userIds={userIds}
                                userNameMap={userNameMap}
                                userStore={userStore}
                                commonStore={commonStore}
                                onClose={onClose}
                            />
                        </Tab>
                    )}

                    {!isBulk && !initialTab && (
                        <Tab eventKey="timesheetBlocks" title={t('Timesheet Blocks')}>
                            <UserTimesheetBlockTab
                                t={t}
                                userId={userId}
                                userStore={userStore}
                                commonStore={commonStore}
                            />
                        </Tab>
                    )}
                </Tabs>
            </Modal.Body>
        </Modal>
    );
};

export default inject('commonStore', 'userStore')(withLocalization(UserWeeklyOvertimeSettingModal));
